// src/hooks/useProfile.ts
import { useCallback } from "react";
import { Profile, SimilarUser } from "@/types/user";
import userService from "@/services/userService";
import useAuthStore from "@/stores/user/authStore";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

export const useProfile = (userId: string | null) => {
  const { user: authUser } = useAuthStore();
  const queryClient = useQueryClient();

  // Determine the target user ID (provided userId or current user's ID)
  const targetUserId = userId || authUser?.user_id;

  // Check if this is the current user's profile
  const isCurrentUser = targetUserId === authUser?.user_id;

  // Fetch profile data using React Query
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ["userProfile", targetUserId],
    queryFn: async () => {
      if (!targetUserId) {
        return { user: null, similar_users: [] };
      }
      return userService.getUserProfile(targetUserId, true);
    },
    enabled: !!targetUserId,
  });

  const profile = data?.user || null;
  const similarUsers = data?.similar_users || [];

  // Follow user mutation
  const followMutation = useMutation({
    mutationFn: (userId: string) => userService.followUser(userId),
    onSuccess: () => {
      // Only invalidate the current user's profile data
      // This ensures we update the following count for the current user
      // without refetching the profile of the user we just followed
      if (authUser?.user_id) {
        queryClient.invalidateQueries({
          queryKey: ["userProfile", authUser.user_id],
          exact: true,
        });
      }
    },
  });

  // Unfollow user mutation
  const unfollowMutation = useMutation({
    mutationFn: (userId: string) => userService.unfollowUser(userId),
    onSuccess: () => {
      // Only invalidate the current user's profile data
      // This ensures we update the following count for the current user
      // without refetching the profile of the user we just unfollowed
      if (authUser?.user_id) {
        queryClient.invalidateQueries({
          queryKey: ["userProfile", authUser.user_id],
          exact: true,
        });
      }
    },
  });

  const followUser = async (userId: string) => {
    return followMutation.mutateAsync(userId);
  };

  const unfollowUser = async (userId: string) => {
    return unfollowMutation.mutateAsync(userId);
  };

  return {
    profile,
    isLoading,
    error,
    isCurrentUser,
    similarUsers,
    followUser,
    unfollowUser,
    refetch,
  };
};
